import { Box, Button, Checkbox, Collapse, Grid, IconButton, Table, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material";
import { Fragment, useState } from "react";
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { DateTimeHelper } from "src/_shares/_helpers/datetime-helper";
import { ProductHelper } from "src/_shares/_helpers/product-helper";
import { FormatHelper } from "src/_shares/_helpers/format-helper";
import { ORDER_STATUS_CODE } from "src/_cores/_constants/order-constants";
import { StatusDisplay } from "src/_shares/_components";

export type TableRowProps = {
    rowData: any,
    isSelected: boolean,
    isAdmin?: boolean,
    onUpdateStatus: (id: number, status: number) => void,
    onDelete: (order: { id: string, orderCode: string }) => void,
    onSelected: (id: number) => void,
    onViewDetail: (id: number) => void,
    onApprove: (id: string) => void,
    onReject: (id: string) => void,
}

export default function OrderItem(props: TableRowProps) {
    const { rowData, isSelected, isAdmin, onDelete, onSelected, onApprove, onReject } = props;
    const [open, setOpen] = useState(false);

    const getFormatedPrice = (price: number) => ProductHelper.getFormatedPrice(price);

    const requestDelete = () => {
        onDelete({ id: rowData.id, orderCode: rowData.orderCode });
    }

    const approve = (id: string) => {
        onApprove(id);
    }

    const reject = (id: string) => {
        onReject(id);
    }

    return (
        <Fragment>
            <TableRow sx={{ '& > *': { borderBottom: 'unset' } }}>
                <TableCell>
                    <Checkbox
                        checked={isSelected}
                        onChange={() => onSelected(rowData.id)}
                        sx={{ display: 'none' }}
                    />
                    <IconButton
                        aria-label="expand row"
                        size="small"
                        onClick={() => setOpen(!open)}
                    >
                        {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                    </IconButton>
                </TableCell>
                <TableCell align="left">{rowData.orderCode}</TableCell>
                <TableCell align="left">{DateTimeHelper.getDateTimeFormated(rowData.createdDate)}</TableCell>
                <TableCell align="left">{rowData.createdBy}</TableCell>
                <TableCell align="left">{getFormatedPrice(rowData.totalPrice)}</TableCell>
                <TableCell align="left">{getFormatedPrice(rowData.totalPriceOnSell)}</TableCell>
                <TableCell align="left">-{getFormatedPrice(rowData.totalDiscount)}</TableCell>
                <TableCell align="left">{getFormatedPrice(rowData.totalFinalPrice)}</TableCell>
                <TableCell align="left">{FormatHelper.getPaymentMethod(rowData.paymentMethod)}</TableCell>
                <TableCell align="left">
                    <StatusDisplay statusCode={rowData.status} />
                </TableCell>
                <TableCell align="center">
                    {rowData.status === ORDER_STATUS_CODE.ORDER_PENDING && (<>
                        <Button
                            style={{ marginRight: '0.5rem' }}
                            variant="outlined"
                            color="primary"
                            onClick={() => approve(rowData.id)}
                        >
                            Xác nhận
                        </Button>
                        <Button
                            variant="contained"
                            color="error"
                            onClick={() => reject(rowData.id)}
                        >
                            Từ chối
                        </Button>
                    </>)}

                    {isAdmin && (
                        <Button
                            style={{ marginLeft: '0.5rem' }}
                            variant="outlined"
                            color="error"
                            onClick={requestDelete}
                        >
                            Xóa
                        </Button>
                    )}
                </TableCell>
            </TableRow>
            <TableRow sx={{ '& > *': { borderBottom: 'unset' } }}>
                <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={12}>
                    <Collapse in={open} timeout="auto" unmountOnExit>
                        <Box sx={{ margin: 1 }}>
                            <Grid container spacing={2} sx={{ marginBottom: 2 }}>
                                <Grid item xs={12} sm={12}>
                                    <Typography variant="h6" gutterBottom component="div">
                                        Mã giảm giá
                                    </Typography>
                                    <Table size="small">
                                        <TableRow>
                                            <TableCell component="th" scope="row">
                                                {rowData.orderVouchers.join(', ')}
                                            </TableCell>
                                        </TableRow>
                                    </Table>
                                </Grid>
                                <Grid item xs={12} sm={12}>
                                    <Typography variant="h6" gutterBottom component="div">
                                        Chi tiết
                                    </Typography>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Tên</TableCell>
                                                <TableCell>Đơn giá</TableCell>
                                                <TableCell align="right">Giá đã giảm</TableCell>
                                                <TableCell align="center">Số lượng</TableCell>
                                                <TableCell align="right">Tạm tính</TableCell>
                                                <TableCell align="right">Tạm tính giá đã giảm</TableCell>
                                                <TableCell align="right">Giảm giá từ voucher</TableCell>
                                                <TableCell align="right">Thành tiền</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {rowData.orderDetails?.map((item: any) => (
                                                <TableRow key={item.productId}>
                                                    <TableCell component="th" scope="row">
                                                        {item.productName} {item.options ? `(${item.options})` : ''}
                                                    </TableCell>
                                                    <TableCell>{getFormatedPrice(item.price)}</TableCell>
                                                    <TableCell align="right">{getFormatedPrice(item.priceOnSell)}</TableCell>
                                                    <TableCell align="center">{item.qty}</TableCell>
                                                    <TableCell align="right">{getFormatedPrice(item.totalPrice)}</TableCell>
                                                    <TableCell align="right">{getFormatedPrice(item.totalPriceOnSell)}</TableCell>
                                                    <TableCell align="right">-{getFormatedPrice(item.discountFromVoucher)}</TableCell>
                                                    <TableCell align="right">{getFormatedPrice(item.totalFinalPrice)}</TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </Grid>
                                <Grid item xs={12} sm={12}>
                                    <Typography variant="h6" gutterBottom component="div">
                                        Thông tin giao hàng
                                    </Typography>
                                    <Table size="small">
                                        <TableBody>
                                            <TableRow>
                                                <TableCell component="th" scope="row">Người nhận:</TableCell>
                                                <TableCell component="th" scope="row">{rowData.fullName || ''}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell component="th" scope="row">Số điện thoại:</TableCell>
                                                <TableCell component="th" scope="row">{rowData.phoneNumber || ''}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell component="th" scope="row">Địa chỉ:</TableCell>
                                                <TableCell component="th" scope="row">
                                                    {rowData.address + ', phường ' + rowData.wardName + ', quận ' + rowData.districtName + ', ' + rowData.cityName}.
                                                </TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell component="th" scope="row">Email:</TableCell>
                                                <TableCell component="th" scope="row">{rowData.email || ''}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell component="th" scope="row">Ghi chú:</TableCell>
                                                <TableCell component="th" scope="row" sx={{ whiteSpace: 'normal' }}>
                                                    {rowData.remark || ''}
                                                </TableCell>
                                            </TableRow>
                                        </TableBody>
                                    </Table>
                                </Grid>
                            </Grid>
                        </Box>
                    </Collapse>
                </TableCell>
            </TableRow>
        </Fragment>
    );
}
